import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { IDeferred, ILogService, IPromise, IQService, toJson } from 'angular';
import { filter, map } from 'rxjs';

import { CSR_FACADE_PREFIX } from '@contact/upgrade';

const MODULE_NAME = 'videoconference';

export interface IVideoconferenceService {
  callInProcess: boolean;
  codeSent: boolean;

  init(): void;

  openVCModal(): void;

  getUsecases(): IPromise<IUsecase[]>;

  openConsole(): void;

  closeConsole(): void;

  hangUp(): void;

  sendCode(usecaseId: string, contactNumber: string): void;

  cancelCode(): void;

  openVideo(): void;

  closeVideo(): void;
}

export class VideoconferenceService implements IVideoconferenceService {
  static $inject: Array<string> = ['$log', '$q', '$uibModal', 'httpDeprecated', 'externalApplications'];

  public name = MODULE_NAME;
  public callInProcess = false;
  public codeSent = false;
  public connected = false;
  private sightcallConsole: SightcallConsole;
  private usecases: IDeferred<IUsecase[]> = this.$q.defer();

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $uibModal: any,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly externalApplications: AppExternalService
  ) {}

  init(): void {
    this.externalApplications
      .applicationAdded$()
      .pipe(
        map(iframe => iframe.id),
        filter(id => id === 'vcFrame')
      )
      .subscribe(id => {
        this.sightcallConsole = new SightcallConsole({ mode: 'iframe', target: id });
        this.sightcallConsole.on('usecases.all', usecaseList => this.usecaseListReceived(usecaseList));
        this.sightcallConsole.on('console.requireAuth', () => this.authenticate());
        this.sightcallConsole.on('console.authSuccess', () => this.authSuccess());
        this.sightcallConsole.on('console.authError', () => this.authError());
        this.sightcallConsole.on('call.created', () => this.callCreated());
        this.sightcallConsole.on('call.terminated', () => this.callTerminated());
        this.sightcallConsole.on('code.created', () => this.codeCreated());
        this.sightcallConsole.on('code.canceled', () => this.codeCanceled());
        this.sightcallConsole.on('console.opened', () => this.consoleOpened());
        this.sightcallConsole.on('plugin.missing', () => this.pluginMissing());
        this.sightcallConsole.on('plugin.connected', () => this.pluginConnected());
        this.sightcallConsole.on('extension.missing', () => this.pluginMissing());
        this.openConsole();
      });
  }

  openConsole(): void {
    this.sightcallConsole.open();
    this.connected = true;
  }

  closeConsole(): void {
    if (this.connected) {
      this.sightcallConsole.disconnect();
      this.sightcallConsole.close();
      this.connected = false;
    }
  }

  getUsecases(): IPromise<IUsecase[]> {
    return this.usecases.promise;
  }

  openVCModal(): void {
    this.$uibModal.open({
      component: 'videoconferenceRequest',
      keyboard: false,
      ariaLabelledBy: 'modal-header',
      ariaDescribedBy: 'modal-body',
    });
  }

  hangUp(): void {
    this.sightcallConsole.hangup();
  }

  sendCode(usecaseId: string, contactNumber: string): void {
    // Use Sightcall server to send code
    this.sightcallConsole.createCode({ usecase: usecaseId, media: 'sms', contact: contactNumber });
  }

  cancelCode(): void {
    this.sightcallConsole.cancelCode();
  }

  openVideo(): void {
    this.httpDeprecated
      .asPromise()
      .get(`${CSR_FACADE_PREFIX}/openVideo`)
      .then(
        response => {
          if (response.data !== 'true') {
            this.$log.warn('[VC] cannot open video');
          }
        },
        () => {
          this.$log.warn('[VC] cannot open video');
        }
      );
  }

  closeVideo(): void {
    this.httpDeprecated
      .asPromise()
      .get(`${CSR_FACADE_PREFIX}/closeVideo`)
      .then(
        response => {
          if (response.data !== 'true') {
            this.$log.warn('[VC] cannot close video');
          }
        },
        () => {
          this.$log.warn('[VC] cannot close video');
        }
      );
  }

  private usecaseListReceived(usecaseList: IUsecase[]): void {
    this.$log.debug('[VC] usecase list received: ' + toJson(usecaseList));
    if (usecaseList.length === 0) {
      this.usecases.reject();
    } else {
      this.usecases.resolve(usecaseList);
    }
  }

  private authenticate(): void {
    this.$log.info('[VC] require auth');
    this.httpDeprecated
      .asPromise()
      .get('V2/protected/video/getVideoUserToken')
      .then(
        response => {
          if (response.status === 200) {
            this.sightcallConsole.connect({ auth_mode: 'internal', temporary_token: response.data });
          } else {
            this.$log.warn('[VC] cannot get video user token');
          }
        },
        () => {
          this.$log.warn('[VC] cannot get video user token');
        }
      );
  }

  private authSuccess(): void {
    this.$log.info('[VC] auth success');
  }

  private authError(): void {
    this.$log.info('[VC] auth error');
  }

  private callCreated(): void {
    this.$log.info('[VC] call created');
    this.callInProcess = true;
    this.codeSent = false;
  }

  private callTerminated(): void {
    this.$log.info('[VC] call terminated');
    this.closeVideo();
    this.callInProcess = false;
    this.codeSent = false;
  }

  private codeCreated(): void {
    this.$log.info('[VC] code created');
    this.openVideo();
    this.codeSent = true;
  }

  private codeCanceled(): void {
    this.$log.info('[VC] code canceled');
    this.closeVideo();
    this.codeSent = false;
  }

  private consoleOpened(): void {
    this.$log.info('[VC] console opened');
  }

  private pluginMissing(): void {
    this.$log.info('[VC] plugin missing');
    this.externalApplications.showApplication('vcFrame');
  }

  private pluginConnected(): void {
    this.$log.info('[VC] plugin connected');
    this.externalApplications.hideApplication('vcFrame');
    this.sightcallConsole.getUsecases();
    window.setTimeout(() => {
      this.usecases.reject();
    }, 5000);
  }
}

export default MODULE_NAME;
